/**
 * Admin → eCom content import (Alex, MOT-812).
 *
 * Upload the C-Series product export Motzu pulled from the store's back
 * office, or download a from-scratch file. The server fills Motzu content
 * for linked Lightspeed products and does not call the eCom API.
 */
import React, { useMemo, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, makeApiCall, readJsonResponse } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import { EmptyState, InlineMessage, LoadingState, PageContainer, PageHeader, SectionCard } from '../components/ui';

interface StoreRow {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
}

interface IntegrationRow {
  id: string;
  platform: 'lightspeed' | 'lightspeed_ecom';
  name: string;
  status: string | null;
}

interface ImportSummary {
  source: 'export' | 'template';
  rowsIn: number;
  matched: number;
  written: number;
  skippedUnmatched: number;
  skippedUnchanged: number;
  skippedNoContent: number;
  skippedAmbiguous: number;
  skippedIdMismatch: number;
  skippedMissingIds: number;
  columnsOmittedToAvoidBlanks: string[];
  unmatchedSample: string[];
  linkedProducts: number;
}

interface ImportFile {
  csv: string;
  filename: string;
  summary: ImportSummary;
}

const platformLabel = (platform: IntegrationRow['platform']): string =>
  platform === 'lightspeed_ecom' ? 'Lightspeed eCom' : 'Lightspeed Retail';

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const AdminLightspeedEcomContentImportPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [storeQuery, setStoreQuery] = useState('');
  const [store, setStore] = useState<StoreRow | null>(null);
  const [integrationId, setIntegrationId] = useState('');
  const [language, setLanguage] = useState('US');
  const [sku, setSku] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stores = useQuery({
    queryKey: ['admin-ecom-content-import-stores', storeQuery],
    enabled: storeQuery.trim().length >= 2,
    queryFn: async (): Promise<StoreRow[]> => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ search: storeQuery.trim(), limit: '8', page: '1' });
      const response = await makeApiCall(`/admin/organizations/store-accounts?${params}`, { headers });
      const body = await readJsonResponse<{ data?: { rows?: StoreRow[] } }>(response, t('admin.ecomContentImport.failed'));
      if (!response.ok) throw new Error(t('admin.ecomContentImport.failed'));
      return body.data?.rows || [];
    },
  });

  const integrations = useQuery({
    queryKey: ['admin-ecom-content-import-integrations', store?.id],
    enabled: Boolean(store?.id),
    queryFn: async (): Promise<IntegrationRow[]> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/admin/organizations/${encodeURIComponent(store!.id)}/lightspeed-ecom-content-import/integrations`,
        { headers },
      );
      const body = await readJsonResponse<{ data?: IntegrationRow[]; error?: { message?: string } }>(
        response,
        t('admin.ecomContentImport.failed'),
      );
      if (!response.ok) throw new Error(body.error?.message || t('admin.ecomContentImport.failed'));
      return body.data || [];
    },
  });

  const integration = useMemo(
    () => (integrations.data || []).find((row) => row.id === integrationId) || null,
    [integrations.data, integrationId],
  );

  const build = useMutation({
    mutationFn: async (kind: 'export' | 'template'): Promise<ImportFile> => {
      if (!store || !integrationId) throw new Error(t('admin.ecomContentImport.failed'));
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (language.trim()) params.set('language', language.trim());
      if (sku.trim()) params.set('sku', sku.trim());
      const path = `/admin/organizations/${encodeURIComponent(store.id)}/integrations/${encodeURIComponent(integrationId)}/lightspeed-ecom-content-import?${params}`;
      const response = kind === 'export'
        ? await makeApiCall(path, {
            method: 'POST',
            headers,
            body: (() => {
              const form = new FormData();
              form.append('file', file as File);
              if (language.trim()) form.append('language', language.trim());
              if (sku.trim()) form.append('sku', sku.trim());
              return form;
            })(),
          })
        : await makeApiCall(path, { headers });
      const body = await readJsonResponse<{ data?: ImportFile; error?: { message?: string } }>(
        response,
        t('admin.ecomContentImport.failed'),
      );
      if (!response.ok || !body.data?.csv) throw new Error(body.error?.message || t('admin.ecomContentImport.failed'));
      return body.data;
    },
    onSuccess: (result) => {
      downloadCsv(result.filename, result.csv);
      setSummary(result.summary);
      setError(null);
      showToast(t('admin.ecomContentImport.filled', { filename: result.filename }), 'success');
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const pickStore = (row: StoreRow) => {
    setStore(row);
    setStoreQuery(row.name);
    setIntegrationId('');
    setSummary(null);
    setError(null);
  };

  return (
    <PageContainer maxWidth={880}>
      <PageHeader title={t('admin.ecomContentImport.title')} description={t('admin.ecomContentImport.description')} />
      <SectionCard title={t('admin.ecomContentImport.title')} padded>
        <Stack spacing={2}>
          <TextField
            label={t('admin.ecomContentImport.store')}
            placeholder={t('admin.ecomContentImport.storePlaceholder')}
            value={storeQuery}
            onChange={(event) => {
              setStoreQuery(event.target.value);
              if (store && event.target.value !== store.name) setStore(null);
            }}
            fullWidth
          />
          {stores.data && stores.data.length > 0 && (!store || storeQuery !== store.name) && (
            <Stack spacing={0.5}>
              {stores.data.map((row) => (
                <Button key={row.id} variant="text" onClick={() => pickStore(row)} sx={{ justifyContent: 'flex-start' }}>
                  {row.name}
                  {row.city ? ` · ${row.city}` : ''}
                  {row.state ? `, ${row.state}` : ''}
                </Button>
              ))}
            </Stack>
          )}
          {store && integrations.isLoading && <LoadingState />}
          {store && integrations.isError && (
            <InlineMessage severity="error">{(integrations.error as Error).message}</InlineMessage>
          )}
          {store && integrations.data && integrations.data.length === 0 && (
            <EmptyState
              size="compact"
              title={t('admin.ecomContentImport.noIntegration')}
              description={t('admin.ecomContentImport.noIntegrationBody')}
            />
          )}
          {store && integrations.data && integrations.data.length > 0 && (
            <TextField
              select
              label={t('admin.ecomContentImport.integration')}
              value={integrationId}
              onChange={(event) => {
                setIntegrationId(event.target.value);
                setSummary(null);
              }}
              fullWidth
            >
              {integrations.data.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name} · {platformLabel(row.platform)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label={t('admin.ecomContentImport.language')}
            value={language}
            onChange={(event) => setLanguage(event.target.value.toUpperCase().slice(0, 2))}
            helperText={t('admin.ecomContentImport.languageHelp')}
            inputProps={{ maxLength: 2 }}
            fullWidth
          />
          <TextField
            label={t('admin.ecomContentImport.sku')}
            value={sku}
            onChange={(event) => setSku(event.target.value)}
            helperText={t('admin.ecomContentImport.skuHelp')}
            fullWidth
          />
          <Box>
            <Button variant="outlined" component="label">
              {file ? file.name : t('admin.ecomContentImport.upload')}
              <input
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  setFile(event.target.files?.[0] || null);
                  setSummary(null);
                }}
              />
            </Button>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              disabled={!integration || !file || build.isPending}
              onClick={() => build.mutate('export')}
            >
              {t('admin.ecomContentImport.fill')}
            </Button>
            <Button
              variant="outlined"
              disabled={!integration || build.isPending}
              onClick={() => build.mutate('template')}
            >
              {t('admin.ecomContentImport.template')}
            </Button>
          </Stack>
          {error && <InlineMessage severity="error">{error}</InlineMessage>}
          {summary && (
            <Stack spacing={1}>
              <InlineMessage severity={summary.written > 0 ? 'success' : 'warning'}>
                {t('admin.ecomContentImport.summary', {
                  written: summary.written,
                  matched: summary.matched,
                  skippedUnmatched: summary.skippedUnmatched,
                  skippedUnchanged: summary.skippedUnchanged,
                })}
              </InlineMessage>
              {summary.skippedMissingIds > 0 && (
                <InlineMessage severity="warning">
                  {t('admin.ecomContentImport.missingIds', { count: summary.skippedMissingIds })}
                </InlineMessage>
              )}
              {summary.skippedIdMismatch > 0 && (
                <InlineMessage severity="warning">
                  {t('admin.ecomContentImport.idMismatch', { count: summary.skippedIdMismatch })}
                </InlineMessage>
              )}
              {summary.skippedAmbiguous > 0 && (
                <InlineMessage severity="warning">
                  {t('admin.ecomContentImport.ambiguous', { count: summary.skippedAmbiguous })}
                </InlineMessage>
              )}
              {summary.columnsOmittedToAvoidBlanks.length > 0 && (
                <InlineMessage severity="info">
                  {t('admin.ecomContentImport.omitted', { columns: summary.columnsOmittedToAvoidBlanks.join(', ') })}
                </InlineMessage>
              )}
              {summary.unmatchedSample.length > 0 && (
                <InlineMessage severity="info">
                  {t('admin.ecomContentImport.unmatched', { sample: summary.unmatchedSample.join(', ') })}
                </InlineMessage>
              )}
              <InlineMessage severity="info">{t('admin.ecomContentImport.importNote')}</InlineMessage>
            </Stack>
          )}
          {integration && (
            <InlineMessage severity="info">{t('admin.ecomContentImport.uploadHelp')}</InlineMessage>
          )}
        </Stack>
      </SectionCard>
    </PageContainer>
  );
};

export default AdminLightspeedEcomContentImportPage;
